'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Search, Menu, X, ChevronDown, TrendingUp, User, LogOut, Settings, Shield, Code2, Lock, Bell, MessageCircle, Type, Palette, Square, Zap,
  LayoutDashboard, SlidersHorizontal, Calendar, DollarSign, Scissors, Eye, Landmark, Brain, ArrowLeftRight,
  PieChart, Rocket, Clock, TrendingDown, Activity, Sunrise, Moon, BarChart2, CalendarDays, Link2, Gauge,
  Briefcase, Sparkles, Bitcoin, Trophy, Radar, Home, Newspaper, Target, Layers, PanelRight, Terminal, FileWarning,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWatchlistPanel } from '@/context/WatchlistPanelContext';
import { usePreferences } from '@/context/PreferencesContext';
import { useSite, useFeatures, usePageAccess } from '@/context/SiteContext';
import { searchStocks, adminChatGetStats, getPublicPortfolioSettings, getHeaders, getNotificationMuteStatus, muteNotifications, unmuteNotifications, getStocksBatch, getCommodityPrices, getWatchlistTrends, type NotificationMuteStatus, type TrendSummary } from '@/lib/api-client';
import { useDashboardTick } from '@/hooks/useDashboardTick';
import { useRecentTickers } from '@/hooks/useRecentTickers';
import { rememberTickerType } from '@/lib/tickerTypeCache';
import { EtfBadge } from '@/components/ticker/EtfBadge';
import { cn, timeAgo } from '@/lib/utils';
import {
  buildHeaderChips,
  formatChipPct,
  chipColorClass,
  type BatchStockLike,
  type CommodityLike,
} from '@/lib/headerMarketIndicators';

// ── Market status (uses shared marketTime) ───────────────────────────────────

import {
  getETTimeParts, getETDate, getMarketPhase as getPhase, getNextMarketOpen,
  isMarketOpen, MARKET_CLOSE_SECS,
} from '@/lib/marketTime';
import { getHolidayName } from '@/lib/marketHolidays';

const FIVE_MINUTES = 300;

function pad2(n: number) { return String(n).padStart(2, '0'); }

function formatCountdown(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (secs <= FIVE_MINUTES) {
    return h > 0 ? `${h}h ${pad2(m)}m ${pad2(s)}s` : `${pad2(m)}:${pad2(s)}`;
  }
  return h > 0 ? `${h}h ${pad2(m)}m` : `${pad2(m)}m`;
}

type MarketInfo = { open: boolean; label: string; sub: string };

export function computeMarketInfo(now = new Date()): MarketInfo {
  // Open/closed comes from the shared isMarketOpen() — the SAME function
  // useMarketOpen() (and thus the ticker tape) uses — so the navbar lock
  // and the tape can never disagree. It's both weekend- and holiday-aware.
  if (isMarketOpen(now)) {
    const { totalSecs } = getETTimeParts(now);
    const secsLeft = MARKET_CLOSE_SECS - totalSecs;
    return { open: true, label: 'Markets Open', sub: `Closes in ${formatCountdown(secsLeft)}` };
  }

  // Closed — surface the holiday name when today is a listed full-day
  // closure (authoritative table, shared with MarketPulseTile), otherwise
  // count down to the next holiday/weekend-aware open. Labeling off the
  // returned weekday skips a holiday the day before next open (e.g.
  // Memorial Day) instead of counting down to a non-trading day.
  const holiday = getHolidayName(getETDate(now));
  if (holiday) {
    return { open: false, label: holiday, sub: 'Market Closed' };
  }

  const { secondsUntil: secsUntilOpen, weekday: openWeekday } = getNextMarketOpen(now);
  const sub = secsUntilOpen > 18 * 3600
    ? `Opens ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][openWeekday]} 9:30 AM ET`
    : `Opens in ${formatCountdown(secsUntilOpen)}`;

  return { open: false, label: 'Markets Closed', sub };
}

function getMarketPhaseInfo(): { phase: 'pre' | 'main' | 'post' | 'closed'; etTime: string; etHour: number; etMinute: number } {
  const { hour: h, minute: m } = getETTimeParts();
  const etTime = `${pad2(h)}:${pad2(m)}`;
  const phase = getPhase();
  return { phase, etTime, etHour: h, etMinute: m };
}

/** Desktop-only header chips — up to 5 user-configured stocks plus an
 *  optional OIL (CL=F) chip. Configured in /account/configurations.
 *  Polled every 60s using three cached endpoints: getStocksBatch for
 *  daily %, getCommodityPrices for OIL, getWatchlistTrends for the
 *  30-min direction arrow on each stock chip. The OIL chip has no
 *  trend arrow — CL=F futures aren't in the intraday bar cache.
 *  Returns null when the master toggle is off. */
function MarketIndicatorsPill() {
  const { headerMarketIndicators } = usePreferences();
  const [batch, setBatch] = useState<BatchStockLike[]>([]);
  const [commodities, setCommodities] = useState<CommodityLike[]>([]);
  const [trends, setTrends] = useState<Record<string, TrendSummary | null>>({});

  const stockTickers = useMemo(
    () => headerMarketIndicators.tickers.map((t) => t.toUpperCase()),
    [headerMarketIndicators.tickers],
  );
  const includeOil = headerMarketIndicators.includeOil;
  const enabled = headerMarketIndicators.enabled;
  // Stable identity for the polling effect — only re-fire when the set
  // of tickers actually changes (joining via comma keeps useMemo / dep
  // checks cheap and ordering-stable since the array is already saved
  // in user-chosen order).
  const stockKey = stockTickers.join(',');

  useEffect(() => {
    if (!enabled) return;
    let stale = false;
    const tick = async () => {
      const [batchRes, commoditiesRes, trendsRes] = await Promise.all([
        stockTickers.length > 0
          ? getStocksBatch(stockTickers).catch(() => [] as BatchStockLike[])
          : Promise.resolve<BatchStockLike[]>([]),
        includeOil
          ? getCommodityPrices().catch(() => [] as CommodityLike[])
          : Promise.resolve<CommodityLike[]>([]),
        stockTickers.length > 0
          ? getWatchlistTrends(stockTickers).catch(() => ({} as Record<string, TrendSummary | null>))
          : Promise.resolve<Record<string, TrendSummary | null>>({}),
      ]);
      if (stale) return;
      setBatch(batchRes);
      setCommodities(commoditiesRes);
      setTrends(trendsRes);
    };
    void tick();
    const id = setInterval(tick, 60_000);
    return () => { stale = true; clearInterval(id); };
  }, [enabled, includeOil, stockKey, stockTickers]);

  const chips = useMemo(
    () => buildHeaderChips(headerMarketIndicators, batch, commodities, trends),
    [headerMarketIndicators, batch, commodities, trends],
  );

  if (!enabled || chips.length === 0) return null;

  return (
    <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
      {chips.map((chip) => {
        const slope = chip.trend?.slope;
        return (
          <Link
            key={`${chip.kind}-${chip.label}`}
            href={chip.href}
            title={
              chip.kind === 'commodity'
                ? 'Crude Oil WTI (CL=F) — click for commodities'
                : `${chip.label} — click to open`
            }
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-card-bg border border-default hover:border-muted transition-colors"
          >
            <span className="text-[10px] font-semibold tracking-wide text-fg-muted">{chip.label}</span>
            <span className={cn('text-xs font-mono font-semibold tabular-nums', chipColorClass(chip.changePct))}>
              {chip.changePct != null ? formatChipPct(chip.changePct) : '—'}
            </span>
            {slope === 'up' && (
              <TrendingUp size={11} className="text-emerald-400 flex-shrink-0" aria-label="Up over the last 30 minutes" />
            )}
            {slope === 'down' && (
              <TrendingDown size={11} className="text-red-400 flex-shrink-0" aria-label="Down over the last 30 minutes" />
            )}
          </Link>
        );
      })}
    </div>
  );
}

function MarketStatusPill() {
  const [info, setInfo] = useState<MarketInfo | null>(null);
  const [phase, setPhase] = useState<ReturnType<typeof getMarketPhaseInfo> | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInfo(computeMarketInfo());
    setPhase(getMarketPhaseInfo());
    const t = setInterval(() => { setInfo(computeMarketInfo()); setPhase(getMarketPhaseInfo()); }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  if (!info || !phase) return null;

  // Timeline position: 0-24 hours mapped to percentage
  const timePercent = ((phase.etHour * 60 + phase.etMinute) / 1440) * 100;

  const phaseColors = { pre: 'text-orange-400', main: 'text-emerald-400', post: 'text-violet-400', closed: 'text-fg-subtle' };
  const phaseLabels = { pre: 'Pre-market', main: 'Main market', post: 'Post-market', closed: 'Closed' };

  return (
    <div ref={ref} className="relative hidden md:block">
      <button
        onClick={() => setDropdownOpen((o) => !o)}
        data-testid="market-status-toggle"
        aria-label={`Market status: ${info.label}, ${info.sub}`}
        className={cn(
          'inline-flex items-center gap-1.5 border text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap cursor-pointer transition-colors',
          info.open
            ? 'bg-green-500/10 border-green-500/20 text-green-400 hover:bg-green-500/20'
            : 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20',
        )}
      >
        <Lock size={13} className="flex-shrink-0" />
        <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', info.open ? 'bg-green-400 animate-pulse' : 'bg-red-400')} />
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-card-bg border border-default rounded-xl shadow-2xl z-50 p-4">
          {/* Phase header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🇺🇸</span>
            <div>
              <div className={cn('text-sm font-semibold', phaseColors[phase.phase])}>{phaseLabels[phase.phase]}</div>
              <div className="text-[11px] text-fg-subtle" data-testid="market-status-sub">{info.label} · {info.sub}</div>
            </div>
          </div>

          {/* Timeline bar */}
          <div className="mt-3 mb-1 relative">
            <div className="flex h-2 rounded-full overflow-hidden bg-surface">
              {/* Pre-market: 4:00-9:30 = 16.7%-39.6% */}
              <div className="bg-surface" style={{ width: '16.7%' }} />
              <div className="bg-orange-500/60" style={{ width: '22.9%' }} />
              {/* Main market: 9:30-16:00 = 39.6%-66.7% */}
              <div className="bg-emerald-500/70" style={{ width: '27.1%' }} />
              {/* Post-market: 16:00-20:00 = 66.7%-83.3% */}
              <div className="bg-violet-500/50" style={{ width: '16.6%' }} />
              <div className="bg-surface" style={{ width: '16.7%' }} />
            </div>
            {/* Current time indicator */}
            <div
              className="absolute top-[-3px] w-0.5 h-[14px] bg-white rounded-full"
              style={{ left: `${timePercent}%` }}
            />
            <div
              className="absolute top-[-16px] text-[9px] text-white font-mono font-bold"
              style={{ left: `${timePercent}%`, transform: 'translateX(-50%)' }}
            >
              {phase.etTime}
            </div>
          </div>
          <div className="flex justify-between text-[9px] text-slate-600 mt-1 mb-3">
            <span>00:00</span>
            <span>09:30</span>
            <span>16:00</span>
            <span>23:59</span>
          </div>

          {/* Trading hours */}
          <div className="bg-surface rounded-lg p-3 space-y-1.5">
            <div className="text-[11px] font-medium text-slate-300 mb-2">Trading Hours (Mon–Fri)</div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-orange-400 font-medium w-20">Pre-market</span>
              <span className="text-fg-muted font-mono">04:00 – 09:30</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-emerald-400 font-medium w-20">Main market</span>
              <span className="text-fg-muted font-mono">09:30 – 16:00</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-violet-400 font-medium w-20">Post-market</span>
              <span className="text-fg-muted font-mono">16:00 – 20:00</span>
            </div>
          </div>

          <p className="text-[9px] text-slate-600 mt-2 text-center">America/New_York (ET)</p>
        </div>
      )}
    </div>
  );
}

interface DropdownItem {
  label: string;
  href: string;
  description?: string;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
}

interface NavItem {
  label: string;
  href?: string;
  dropdown?: DropdownItem[];
  gold?: boolean;
}

function MobileAccordion({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold tracking-wider uppercase text-slate-300 hover:text-white hover:bg-white/5 rounded-lg"
      >
        {label}
        <ChevronDown size={14} className={cn('text-fg-subtle transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="pb-1">{children}</div>}
    </div>
  );
}

const navItems: NavItem[] = [
  {
    label: 'Stocks',
    dropdown: [
      { label: 'Screener', href: '/stocks/screener', description: 'Filter stocks by criteria', icon: SlidersHorizontal },
      { label: 'Signal Flips', href: '/stocks/signal-flips', description: 'Recent Buy/Hold/Sell trend changes', icon: Zap },
      { label: 'Earnings Calendar', href: '/stocks/earnings', description: 'Upcoming earnings dates', icon: Calendar },
      { label: 'Dividend Calendar', href: '/dividends', description: 'Upcoming ex-dividend dates', icon: DollarSign },
      { label: 'Stock Splits', href: '/splits', description: 'Upcoming & recent splits', icon: Scissors },
      { label: 'Recent Offerings', href: '/offerings', description: 'SEC dilution filings (S-1/S-3/424B5)', icon: FileWarning },
      { label: 'Insider Trading', href: '/insiders', description: 'Recent insider transactions', icon: Eye },
      { label: 'Congress Trading', href: '/congress', description: 'Congressional stock trades', icon: Landmark },
      { label: 'Smart Money', href: '/smart-money', description: 'Insider + Congress aligned trades', icon: Brain },
      { label: 'Compare', href: '/stocks/compare', description: 'Compare multiple stocks', icon: ArrowLeftRight },
    ],
  },
  {
    label: 'ETFs',
    dropdown: [
      { label: 'Screener', href: '/etf/screener', description: 'Filter ETFs by criteria', icon: SlidersHorizontal },
      { label: 'Setups', href: '/etf/setups', description: 'Daily-bar pullback patterns (Connors)', icon: Activity },
      { label: 'Compare', href: '/etf/compare', description: 'Compare multiple ETFs', icon: ArrowLeftRight },
    ],
  },
  {
    label: 'IPOs',
    dropdown: [
      { label: 'Recent IPOs', href: '/ipos?tab=recent', description: 'Recently listed companies', icon: Clock },
      { label: 'Upcoming IPOs', href: '/ipos?tab=upcoming', description: 'Expected listings', icon: Rocket },
      { label: 'IPO Calendar', href: '/ipos', description: 'Full IPO calendar', icon: Calendar },
    ],
  },
  {
    label: 'Markets',
    dropdown: [
      // After Hours sits at the top — combined Pre/After-Hours view
      // (issue #224). The standalone Pre-Market Movers entry was
      // dropped: it hit `/markets/premarket` which at most hours of
      // the day has no live extended-hours data and surfaced as an
      // empty table to users.
      { label: 'After Hours', href: '/markets/afterhours', description: 'Pre / after-hours movers', icon: Moon },
      { label: 'Top Gainers', href: '/markets/gainers', description: "Today's biggest winners", icon: TrendingUp },
      { label: 'Top Losers', href: '/markets/losers', description: "Today's biggest losers", icon: TrendingDown },
      { label: 'Most Active', href: '/markets/active', description: 'Highest volume stocks', icon: Activity },
      { label: 'Nasdaq 100 Index', href: '/index/%5ENDX', description: 'NDX price and intraday chart', icon: BarChart2 },
      { label: 'Options Plays', href: '/options-plan', description: 'Calls/puts directional setups with entry/stop/target', icon: Target },
      { label: 'Paper Options', href: '/paper-options', description: 'Practice calls/puts with real market data — no real money', icon: Rocket },
      { label: 'Leveraged ETFs', href: '/leveraged-etfs', description: 'Plain / bull / bear ETF comparison + decay-by-days calculator', icon: Layers },
      { label: 'Pre-Market Pulse', href: '/markets/pre-market', description: 'Futures, rates, FX, global indices + AI brief', icon: Sunrise },
      { label: 'Pre-Market AI Digest', href: '/markets/pre-market-digest', description: 'Personalized overnight news for your holdings + watchlist', icon: Sparkles },
      { label: 'Sector Rotation', href: '/sectors', description: 'Performance by sector', icon: PieChart },
      { label: 'Unusual Volume', href: '/unusual-volume', description: 'Small-caps trading on heavy volume', icon: Activity },
      { label: 'Economic Calendar', href: '/economic-calendar', description: 'Fed, CPI, jobs & more', icon: CalendarDays },
      { label: 'Correlation Finder', href: '/correlation', description: 'Find correlated stocks', icon: Link2 },
      { label: 'Market Mood', href: '/mood', description: 'Market mood at a glance', icon: Gauge },
      { label: 'News', href: '/news', description: 'Market news & headlines', icon: Newspaper },
    ],
  },
  { label: 'Pro', href: '/pro', gold: true },
];

export default function Navbar() {
  const { user, logout, isAdmin, isFull, loading } = useAuth();
  const { isOpen: watchlistOpen, toggle: toggleWatchlist } = useWatchlistPanel();
  const { theme, setTheme, largeText, setLargeText, squareTiles, setSquareTiles } = usePreferences();
  const { siteName, logoUrl, logoUrlMobile, brandingLoaded } = useSite();
  const features = useFeatures();
  const { getAccess } = usePageAccess();
  const router = useRouter();

  // Portfolio nav settings (feature-gated)
  const [portfolioNav, setPortfolioNav] = useState<{ congressional: boolean; crypto: boolean }>({ congressional: false, crypto: false });
  useEffect(() => {
    if (!features.portfolioTracker) return;
    getPublicPortfolioSettings()
      .then((s) => setPortfolioNav({ congressional: s.congressionalEnabled, crypto: s.cryptoEnabled }))
      .catch(() => {});
  }, [features.portfolioTracker]);

  // Badge renderer for page access levels
  const renderAccessBadge = (href: string) => {
    const access = getAccess(href);
    if (access === 'pro') return <span className="pro-badge ml-1 text-[8px] leading-none py-0 px-1">PRO</span>;
    if (access === 'full') return <span className="ml-1 text-[8px] leading-none py-0 px-1 rounded bg-amber-400/20 text-amber-300 border border-amber-400/30 font-bold uppercase tracking-wider">FULL</span>;
    if (access === 'registered' && !user) return <Lock size={10} className="ml-1 text-fg-subtle inline-block" />;
    return null;
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openNav, setOpenNav] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alertsRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ ticker: string; name: string; type?: string }[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Recent dropdown — sourced from actually-viewed /stocks/[ticker] page
  // loads (not from search-result selections). Hook handles localStorage
  // persistence + cross-tab sync. Top 5 shown when the search input is
  // focused/clicked with an empty query; once the user types, debounced
  // API-driven suggestions take over.
  const { recentTickers } = useRecentTickers();

  // Alerts bell state
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [triggeredAlerts, setTriggeredAlerts] = useState<{ id: string; symbol: string; condition: string; threshold: number; triggeredAt: string | null; currentPrice: number | null }[]>([]);
  const [congressAlerts, setCongressAlerts] = useState<{ id: string; portfolioId: string; ticker: string | null; actionNeeded: string; representative: string; createdAt: string }[]>([]);
  const [scannerAlerts, setScannerAlerts] = useState<{ ticker: string; triggeredAt: string; severity: 'elevated' | 'high' | 'extreme'; reason: string | null; peakChangePct: number | null; peakRvol: number | null; currentPrice: number | null }[]>([]);
  const [alertCount, setAlertCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [muteStatus, setMuteStatus] = useState<NotificationMuteStatus>({ emailMutedUntil: null, pushMutedUntil: null });
  const [mutePending, setMutePending] = useState<'email' | 'push' | null>(null);

  // Fetch triggered alerts on every dashboard tick (price alerts +
  // congressional-watch). The tick is gated to RTH weekdays, which matches
  // when price alerts can trigger anyway. Own setInterval replaced with the
  // global broadcaster so the cadence is admin-configurable.
  const { tick } = useDashboardTick();
  useEffect(() => {
    if (!user) { setTriggeredAlerts([]); setCongressAlerts([]); setScannerAlerts([]); setAlertCount(0); return; }
    Promise.all([
      fetch('/api/alerts/triggered', { headers: getHeaders(), credentials: 'include' }).then((r) => r.ok ? r.json() : { data: [], count: 0 }).catch(() => ({ data: [], count: 0 })),
      fetch('/api/alerts/congress-triggered', { headers: getHeaders(), credentials: 'include' }).then((r) => r.ok ? r.json() : { data: [], count: 0 }).catch(() => ({ data: [], count: 0 })),
      fetch('/api/alerts/scanner-triggered', { headers: getHeaders(), credentials: 'include' }).then((r) => r.ok ? r.json() : { data: [], count: 0 }).catch(() => ({ data: [], count: 0 })),
    ]).then(([priceAlertsRes, congressAlertsRes, scannerAlertsRes]) => {
      setTriggeredAlerts(priceAlertsRes.data ?? []);
      setCongressAlerts(congressAlertsRes.data ?? []);
      setScannerAlerts(scannerAlertsRes.data ?? []);
      setAlertCount((priceAlertsRes.count ?? 0) + (congressAlertsRes.count ?? 0) + (scannerAlertsRes.count ?? 0));
    });
  }, [user, tick]);

  // Fetch chat unread count for admin
  useEffect(() => {
    if (!isAdmin) { setChatUnread(0); return; }
    const fetchChat = () => {
      adminChatGetStats().then((s) => setChatUnread(s.openCount)).catch(() => {});
    };
    fetchChat();
    const interval = setInterval(fetchChat, 15_000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Mark alerts as seen when dropdown opens
  useEffect(() => {
    if (alertsOpen && alertCount > 0 && user) {
      fetch('/api/alerts/mark-seen', { method: 'POST', headers: getHeaders(), credentials: 'include' }).catch(() => {});
      setAlertCount(0);
    }
  }, [alertsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load notification mute status when the alerts dropdown opens so the
  // footer buttons reflect the live state. Cheap (single SELECT) but no
  // need to keep it polling — it changes only when the user clicks here.
  useEffect(() => {
    if (!alertsOpen || !user) return;
    getNotificationMuteStatus().then(setMuteStatus).catch(() => {});
  }, [alertsOpen, user]);

  const handleMute = async (channel: 'email' | 'push', duration: 'today' | 'week') => {
    setMutePending(channel);
    try {
      const status = await muteNotifications(channel, duration);
      setMuteStatus(status);
    } catch {} finally { setMutePending(null); }
  };
  const handleUnmute = async (channel: 'email' | 'push') => {
    setMutePending(channel);
    try {
      const status = await unmuteNotifications(channel);
      setMuteStatus(status);
    } catch {} finally { setMutePending(null); }
  };

  // Close alerts dropdown on outside click
  useEffect(() => {
    if (!alertsOpen) return;
    const handler = (e: MouseEvent) => {
      if (alertsRef.current && !alertsRef.current.contains(e.target as Node)) setAlertsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [alertsOpen]);

  // Filter nav items based on feature flags + user tier.
  // The "Pro" link is an upsell CTA — hide it from anyone who's already
  // pro/full/vip or admin, since it'd just be noise. Free + anonymous
  // users still see it.
  const filteredNavItems = useMemo(() => {
    const tier = user?.subscriptionTier;
    const alreadyPaid = tier === 'pro' || tier === 'full' || tier === 'vip' || isAdmin;
    const items = navItems.filter((item) => {
      if (item.gold && !features.proSubscriptions) return false;
      if (item.gold && alreadyPaid) return false;
      return true;
    });

    // Inject Portfolio dropdown if feature is enabled, ahead of the
    // gold "Pro" upsell so that always sits at the right edge.
    if (features.portfolioTracker) {
      const portfolioDropdown: typeof items[0] = {
        label: 'Portfolio',
        dropdown: [
          { label: 'My Portfolios', href: '/portfolio', description: 'Track your investments', icon: Briefcase },
          { label: 'Auto-Generate', href: '/portfolio/generate', description: 'Build a portfolio in 3 questions', icon: Sparkles },
          { label: 'Portfolio Challenge', href: '/challenges', description: 'Compete with virtual cash', icon: Trophy },
          ...(isFull ? [{ label: 'Scanner Log', href: '/portfolio/scanner', description: 'Live feed of qualified scanner hits', icon: Radar }] : []),
          ...(isAdmin ? [{ label: 'EOD Pattern Scanner', href: '/scanner/volume-contraction', description: 'Daily-bar pattern scanner — VCP, oversold bounce, capitulation reversal (admin)', icon: Activity }] : []),
          ...(isAdmin ? [{ label: 'Coiled-Spring Scanner', href: '/scanner/coiled-spring', description: 'Compressed charts with positive news catalysts (admin)', icon: Zap }] : []),
          ...(portfolioNav.congressional ? [{ label: 'Congressional Directory', href: '/congress?tab=directory', description: 'Browse members & clone to portfolio', icon: Landmark }] : []),
          ...(portfolioNav.crypto ? [{ label: 'Crypto', href: '/portfolio/crypto', description: 'Crypto portfolio tracking (coming soon)', icon: Bitcoin }] : []),
        ],
      };
      // Sit Portfolio just before any trailing gold (Pro) item; if Pro
      // was filtered out above, append at the end.
      const proIdx = items.findIndex((i) => i.gold);
      items.splice(proIdx >= 0 ? proIdx : items.length, 0, portfolioDropdown);
    }

    // Pro Terminal lives in the right-side toolbar (next to Scanner +
    // Ticker search), not in the nav-items list — see the Right side block
    // below.

    return items;
  }, [features.proSubscriptions, features.portfolioTracker, portfolioNav, isAdmin, isFull, user?.subscriptionTier]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  // Debounced search suggestions
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 1) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }
    const timer = setTimeout(() => {
      const normalized = searchQuery.trim().toLowerCase().replace(/[\s-]+/g, ' ');
      const compact = normalized.replace(/[\s^]/g, '');
      const indexSuggestions = (
        (compact.length >= 2 && 'ndx'.startsWith(compact))
        || (compact.length >= 3 && 'nasdaq100index'.includes(compact))
      )
        ? [{ ticker: '^NDX', name: 'Nasdaq 100 Index', type: 'index' }]
        : [];
      searchStocks(searchQuery.trim())
        .then((results) => {
          const merged = [
            ...indexSuggestions,
            ...results.filter((result) => result.ticker.toUpperCase() !== '^NDX'),
          ].slice(0, 8);
          setSuggestions(merged);
          setSuggestionsOpen(merged.length > 0);
          setSelectedIdx(-1);
          // Phase 3 cache seed — every suggestion carries an asset type
          // and many of them will be clicked or shown adjacent to a
          // ticker in another widget. Seeding on load (not just on
          // click) makes the cache useful even when the user dismisses
          // the dropdown without clicking.
          for (const r of results) {
            if (r.type === 'etf' || r.type === 'stock') rememberTickerType(r.ticker, r.type);
          }
        })
        .catch(() => {
          setSuggestions(indexSuggestions);
          setSuggestionsOpen(indexSuggestions.length > 0);
          setSelectedIdx(-1);
        });
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close all menus on page scroll (but not when scrolling inside a dropdown)
  useEffect(() => {
    const hasOpen = openNav || userMenuOpen || mobileOpen;
    if (!hasOpen) return;
    const onScroll = (e: Event) => {
      const target = e.target as Node;
      // Don't close if scrolling inside the navbar or mobile menu
      if (navRef.current?.contains(target)) return;
      if (mobileMenuRef.current?.contains(target)) return;
      setOpenNav(null);
      setUserMenuOpen(false);
      setMobileOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [openNav, userMenuOpen, mobileOpen]);

  // Close nav dropdown and user menu on outside click
  useEffect(() => {
    if (!openNav && !userMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (openNav && navRef.current && !navRef.current.contains(target)) {
        setOpenNav(null);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openNav, userMenuOpen]);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    router.push('/');
  };

  const navigateToTicker = (ticker: string, type?: string, _name?: string) => {
    const upper = ticker.toUpperCase();
    if (type === 'index' || upper === 'NDX' || upper === '^NDX') {
      router.push(`/index/${encodeURIComponent('^NDX')}`);
      setSearchQuery('');
      setSearchOpen(false);
      setSuggestionsOpen(false);
      setSuggestions([]);
      return;
    }
    const prefix = type === 'etf' ? '/etf' : '/stocks';
    // Phase 3 cache seed — search results carry the canonical type, so
    // every other TickerLink in the app for the same ticker (watchlist,
    // movers, news, etc.) renders the correct URL on first paint after
    // the user has searched for it once.
    if (type === 'etf' || type === 'stock') rememberTickerType(upper, type);
    // Recents are written by the detail page itself on successful load, so
    // we don't push here — typing a ticker that doesn't resolve shouldn't
    // end up in the list.
    router.push(`${prefix}/${upper}`);
    setSearchQuery('');
    setSearchOpen(false);
    setSuggestionsOpen(false);
    setSuggestions([]);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIdx >= 0 && suggestions[selectedIdx]) {
      navigateToTicker(suggestions[selectedIdx].ticker, suggestions[selectedIdx].type, suggestions[selectedIdx].name);
    } else if (searchQuery.trim()) {
      navigateToTicker(searchQuery.trim());
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!suggestionsOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-default">
      <div className="px-2">
        <div className="flex items-center h-14 gap-1 sm:gap-2">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0 min-w-0 overflow-hidden">
            {brandingLoaded && (logoUrl ? (
              // When a mobile-specific icon-only logo exists, show it on
              // narrow viewports for SIGNED-IN users only, so the wordmark
              // + PRO badge don't crowd out the tab buttons next to the
              // logo. Anonymous mobile visitors don't have that toolbar
              // clutter yet and get the full logo for branding. Falls back
              // to the regular logo at sm+ (640px+) and everywhere when no
              // mobile variant is configured.
              logoUrlMobile ? (
                <>
                  <img src={user ? logoUrlMobile : logoUrl} alt={siteName} className="h-7 w-auto object-contain sm:hidden" />
                  <img src={logoUrl} alt={siteName} className="h-7 w-auto object-contain hidden sm:block" />
                </>
              ) : (
                <img src={logoUrl} alt={siteName} className="h-7 w-auto object-contain" />
              )
            ) : (
              <>
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center">
                  <TrendingUp size={16} className="text-white" />
                </div>
                <span className="font-medium text-base bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent">
                  {siteName}
                </span>
              </>
            ))}
          </Link>

          {/* Home shortcut — explicit button so users on detail pages have
           *  an obvious "back to dashboard" affordance separate from the
           *  logo (which is also a link but reads as branding to many).
           *  Bordered + filled like the right-side action buttons (Scanner,
           *  Portfolio) so it visibly reads as a button, not just an icon. */}
          <Link
            href="/"
            onClick={() => setOpenNav(null)}
            title="Home"
            aria-label="Home"
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-md bg-card-bg border border-default text-fg-muted hover:text-white hover:bg-card-hover-bg hover:border-muted transition-colors flex-shrink-0"
          >
            <Home size={16} />
          </Link>

          {/* Desktop Nav — gap-0.5 + per-item px-1.5 puts the visible
           *  spacing between top-level items at ~14px (down from ~26px
           *  with the previous px-3). The desktop nav sits at its
           *  intrinsic width (no flex-1) so the centering wrapper below
           *  has clear ownership of the spare slack between the nav and
           *  the right-side action group — issue #266 added a flex-1
           *  wrapper for the market-indicator chips, and two competing
           *  flex-1 siblings would split the band 50/50 and collide
           *  with the chips at narrow lg widths. */}
          <div ref={navRef} className="hidden lg:flex items-center gap-0.5 flex-shrink-0">
            {filteredNavItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => {
                  if (item.dropdown) {
                    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = setTimeout(() => { setOpenNav(item.label); setUserMenuOpen(false); }, 200);
                  }
                }}
                onMouseLeave={() => {
                  if (item.dropdown) {
                    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = setTimeout(() => setOpenNav(null), 400);
                  }
                }}
              >
                {item.href && !item.dropdown ? (
                  <Link
                    href={item.href}
                    onClick={() => setOpenNav(null)}
                    className={cn(
                      'flex items-center gap-1 px-1.5 py-2 text-xs font-semibold tracking-wider uppercase rounded-md transition-colors',
                      item.gold
                        ? 'text-gold hover:text-gold-light hover:bg-gold/10'
                        : 'text-slate-300 hover:text-white hover:bg-white/5'
                    )}
                  >
                    {item.label}
                    {item.href && renderAccessBadge(item.href)}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => { setOpenNav(openNav === item.label ? null : item.label); setUserMenuOpen(false); }}
                      className={cn(
                        'flex items-center gap-1 px-1.5 py-2 text-xs font-semibold tracking-wider uppercase rounded-md transition-colors',
                        openNav === item.label
                          ? 'text-white bg-white/5'
                          : 'text-slate-300 hover:text-white hover:bg-white/5'
                      )}
                    >
                      {item.label}
                      <ChevronDown size={14} className={cn('transition-transform', openNav === item.label ? 'text-white rotate-180' : 'text-fg-muted')} />
                    </button>
                    {item.dropdown && openNav === item.label && (() => {
                      // Desktop dropdown — chunk into columns of up to 5
                      // entries so dropdowns longer than the fold (Stocks
                      // had 9, the Markets list 12) lay out side-by-side
                      // instead of forcing a vertical scroll.
                      const COLUMN_SIZE = 5;
                      const columns: Array<typeof item.dropdown> = [];
                      for (let i = 0; i < item.dropdown.length; i += COLUMN_SIZE) {
                        columns.push(item.dropdown.slice(i, i + COLUMN_SIZE));
                      }
                      return (
                      <div className="absolute top-full left-0 pt-1 animate-slide-down z-50">
                        <div className="bg-card-bg border border-default rounded-xl shadow-2xl py-1.5 max-h-[calc(100vh-80px)] overflow-y-auto flex divide-x divide-border-default/60">
                          {columns.map((col, ci) => (
                            <div key={ci} className="min-w-[220px] flex flex-col">
                              {col.map((sub) => {
                                const SubIcon = sub.icon;
                                return (
                                <Link
                                  key={sub.href}
                                  href={sub.href}
                                  onClick={() => setOpenNav(null)}
                                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-card-hover-bg transition-colors group/item"
                                >
                                  {SubIcon && <SubIcon size={15} className="text-fg-subtle group-hover/item:text-sky-400 mt-0.5 flex-shrink-0 transition-colors" />}
                                  <div>
                                    <div className="text-sm font-medium text-slate-200 group-hover/item:text-white flex items-center">
                                      {sub.label}
                                      {renderAccessBadge(sub.href)}
                                    </div>
                                    {sub.description && (
                                      <div className="text-xs text-fg-subtle mt-0.5">{sub.description}</div>
                                    )}
                                  </div>
                                </Link>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })()}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Desktop-only market-indicator chips. Wrapped in a flex-1
              centering container so the chips sit dead-center in the
              band between the primary nav and the right-side action
              group, instead of hugging the right-side icons via the
              old `ml-auto` arrangement. The wrapper still renders
              (with zero children) when the pill is disabled or empty,
              so the right group stays anchored to the right edge. */}
          <div className="flex-1 flex justify-center min-w-0">
            <MarketIndicatorsPill />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Portfolio shortcut — same icon-button shape as the Scanner
                shortcut so the two sit as a paired toolbar next to the
                search affordance. Visible to any signed-in user (the
                portfolios index page is the most-trafficked authenticated
                landing destination); admins see Portfolio + Scanner side
                by side. */}
            {user && (
              <Link
                href="/portfolio"
                className="inline-flex items-center justify-center w-8 h-8 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-300 hover:text-emerald-200 transition-all"
                title="My Portfolios"
                aria-label="My Portfolios"
              >
                <Briefcase size={15} className="text-emerald-400" />
              </Link>
            )}
            {/* Scanner shortcut. Lives next to the Ticker search so this
                most-used entry-point is always one click away from anywhere
                in the app, not just the home dashboard. Full-tier+ (the
                scanner re-gate). Visible on mobile too — small icon-only
                button, fits next to the search affordance. */}
            {isFull && (
              <Link
                href="/portfolio/scanner"
                className="inline-flex items-center justify-center w-8 h-8 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-500/30 rounded-lg text-fuchsia-300 hover:text-fuchsia-200 transition-all"
                title="Scanner Log"
                aria-label="Scanner Log"
              >
                <Radar size={15} className="text-fuchsia-400" />
              </Link>
            )}
            {/* Pro Terminal shortcut — sits between the Scanner button and the
                Ticker search. Same icon-button shape/size as Scanner/Portfolio
                (w-8 h-8, icon 15), sky-blue to match the stock-page Terminal
                button. Pro+/admin only. */}
            {(isAdmin || user?.subscriptionTier === 'pro' || user?.subscriptionTier === 'full' || user?.subscriptionTier === 'vip') && (
              <Link
                href="/terminal"
                className="inline-flex items-center justify-center w-8 h-8 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 rounded-lg text-sky-300 hover:text-sky-200 transition-all"
                title="Pro Terminal (day-trading view)"
                aria-label="Pro Terminal"
              >
                <Terminal size={15} className="text-sky-400" />
              </Link>
            )}
            {/* Desktop: search input is ALWAYS visible, compact width — no
                auto-expand on focus (plan #144.8). Clicking/focusing with an
                empty query opens a dropdown of the user's 5 most recent
                ticker selections; typing replaces that with debounced
                server-side suggestions. Mobile: tap-to-open overlay below. */}
            <div ref={searchWrapperRef} className="hidden sm:block relative">
              <form onSubmit={handleSearch} className="flex items-center">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => {
                      // Empty query → open recents; non-empty → existing
                      // API-suggestion dropdown when results are present.
                      if (!searchQuery.trim() && recentTickers.length > 0) setSuggestionsOpen(true);
                      else if (suggestions.length > 0) setSuggestionsOpen(true);
                      setOpenNav(null); setMobileOpen(false);
                    }}
                    onClick={() => {
                      if (!searchQuery.trim() && recentTickers.length > 0) setSuggestionsOpen(true);
                    }}
                    placeholder="Ticker"
                    className="bg-card-bg border border-default rounded-lg pl-7 pr-6 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 w-28"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); }} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-white">
                      <X size={12} />
                    </button>
                  )}
                </div>
              </form>
              {suggestionsOpen && (suggestions.length > 0 || (!searchQuery.trim() && recentTickers.length > 0)) && (
                <div className="absolute top-full right-0 mt-1 w-72 bg-card-bg border border-default rounded-xl shadow-2xl z-50 overflow-hidden">
                  {searchQuery.trim() ? (
                    suggestions.map((s, idx) => (
                      <button key={s.ticker} onMouseDown={(e) => { e.preventDefault(); navigateToTicker(s.ticker, s.type, s.name); }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors', idx === selectedIdx ? 'bg-sky-500/15' : 'hover:bg-card-hover-bg')}>
                        <span className="text-sm font-mono font-semibold text-sky-400 w-14 flex-shrink-0">{s.ticker}</span>
                        {s.type === 'etf' && <EtfBadge />}
                        {s.type === 'index' && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/20">INDEX</span>}
                        <span className="text-xs text-fg-muted truncate">{s.name}</span>
                      </button>
                    ))
                  ) : (
                    <>
                      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-subtle">Recent</div>
                      {recentTickers.map((r) => (
                        <button key={r.ticker} onMouseDown={(e) => { e.preventDefault(); navigateToTicker(r.ticker, r.type, r.name); }} className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-card-hover-bg">
                          <span className="text-sm font-mono font-semibold text-sky-400 w-14 flex-shrink-0">{r.ticker}</span>
                          {r.type === 'etf' && <EtfBadge />}
                          <span className="text-xs text-fg-muted truncate">{r.name}</span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile search: tap-to-open overlay */}
            {searchOpen ? (
              <div className="sm:hidden">
                <button
                  onClick={() => { setSearchOpen(false); setSuggestionsOpen(false); setSearchQuery(''); }}
                  className="p-2 text-sky-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
                <div className="fixed inset-0 top-[56px] bg-black/40 z-40" onClick={() => { setSearchOpen(false); setSuggestionsOpen(false); setSearchQuery(''); }} />
                <div className="fixed left-0 right-0 top-[56px] px-3 py-2 bg-surface border-b border-default z-50">
                  <form onSubmit={handleSearch} className="flex items-center">
                    <div className="relative w-full">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
                      <input
                        autoFocus
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={() => { suggestions.length > 0 && setSuggestionsOpen(true); setOpenNav(null); setMobileOpen(false); }}
                        placeholder="Search ticker..."
                        className="bg-card-bg border border-default rounded-lg pl-8 pr-7 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 w-full"
                        autoComplete="off"
                      />
                      {searchQuery && (
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-white">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </form>
                  {suggestionsOpen && suggestions.length > 0 && (
                    <div className="mt-1 w-full bg-card-bg border border-default rounded-xl shadow-2xl overflow-hidden">
                      {suggestions.map((s, idx) => (
                        <button key={s.ticker} onMouseDown={(e) => { e.preventDefault(); navigateToTicker(s.ticker, s.type, s.name); }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors', idx === selectedIdx ? 'bg-sky-500/15' : 'hover:bg-card-hover-bg')}>
                          <span className="text-sm font-mono font-semibold text-sky-400 w-14 flex-shrink-0">{s.ticker}</span>
                          {s.type === 'etf' && <EtfBadge />}
                          {s.type === 'index' && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/20">INDEX</span>}
                          <span className="text-xs text-fg-muted truncate">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setSearchOpen(true); setOpenNav(null); setUserMenuOpen(false); setMobileOpen(false); }}
                className="sm:hidden p-2 text-fg-muted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                aria-label="Search"
              >
                <Search size={18} />
              </button>
            )}

            {/* Market status — always visible now that desktop search is persistent */}
            <MarketStatusPill />

            {/* Theme toggle */}
            <button
              onClick={() => setTheme(theme === 'dark' || theme === 'gray' || theme === 'contrast' ? 'light' : 'dark')}
              className="hidden sm:flex p-2 text-fg-muted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              aria-label="Toggle theme"
              title={theme === 'dark' || theme === 'gray' || theme === 'contrast' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' || theme === 'gray' || theme === 'contrast' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>

            {/* Watchlist side panel toggle */}
            {user && (
              <button
                onClick={toggleWatchlist}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  watchlistOpen ? 'text-sky-400 bg-sky-500/10' : 'text-fg-muted hover:text-white hover:bg-white/5',
                )}
                aria-label="Toggle watchlist panel"
                aria-pressed={watchlistOpen}
                title="Watchlist panel"
              >
                <PanelRight size={18} />
              </button>
            )}

            {/* Alerts bell */}
            {user && (
              <div ref={alertsRef} className="relative">
                <button
                  onClick={() => { setAlertsOpen(!alertsOpen); setUserMenuOpen(false); setOpenNav(null); }}
                  className="relative p-2 text-fg-muted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  aria-label="Alerts"
                >
                  <Bell size={18} />
                  {(alertCount + chatUnread) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none">
                      {(alertCount + chatUnread) > 99 ? '99+' : alertCount + chatUnread}
                    </span>
                  )}
                </button>
                {alertsOpen && (
                  <>
                  <div className="fixed inset-0 top-[56px] bg-black/40 sm:hidden z-40" onClick={() => setAlertsOpen(false)} />
                  <div className="fixed inset-x-0 top-[56px] mx-2 sm:absolute sm:inset-x-auto sm:mx-0 sm:right-0 sm:top-full sm:mt-1 sm:w-80 bg-card-bg border border-default rounded-b-xl sm:rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-default flex items-center justify-between">
                      <span className="text-sm font-semibold text-white">Alerts</span>
                      <Link href="/account/alerts" onClick={() => setAlertsOpen(false)} className="text-xs text-sky-400 hover:text-sky-300">Manage</Link>
                    </div>
                    {triggeredAlerts.length === 0 && congressAlerts.length === 0 && scannerAlerts.length === 0 && chatUnread === 0 ? (
                      <div className="px-4 py-8 text-center text-xs text-fg-subtle">No recent alerts</div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto divide-y divide-border-default/50">
                        {/* Chat messages */}
                        {isAdmin && chatUnread > 0 && (
                          <Link href="/admin/chat" onClick={() => setAlertsOpen(false)} className="flex items-start gap-3 px-4 py-3 hover:bg-card-hover-bg transition-colors">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <MessageCircle size={14} className="text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white">
                                <span className="font-semibold text-emerald-400">{chatUnread}</span>{' '}
                                <span className="text-slate-300">open chat conversation{chatUnread !== 1 ? 's' : ''}</span>
                              </p>
                              <p className="text-[10px] text-slate-600 mt-0.5">View inbox</p>
                            </div>
                          </Link>
                        )}
                        {/* Congressional watch alerts */}
                        {congressAlerts.map((a) => {
                          const label = a.actionNeeded === 'member_sold_held'
                            ? `${a.representative} sold ${a.ticker ?? 'a held position'}`
                            : a.actionNeeded === 'member_bought_new'
                              ? `${a.representative} opened ${a.ticker ?? 'a new position'}`
                              : `${a.representative} added to ${a.ticker ?? 'a position'}`;
                          const timeStr = a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' : '';
                          return (
                            <Link key={a.id} href={`/portfolio/${a.portfolioId}?tab=watcher`} onClick={() => setAlertsOpen(false)} className="flex items-start gap-3 px-4 py-3 hover:bg-card-hover-bg transition-colors">
                              <div className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Landmark size={14} className="text-sky-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">{label}</p>
                                <p className="text-[10px] text-slate-600 mt-0.5">{timeStr}</p>
                              </div>
                            </Link>
                          );
                        })}
                        {/* Price alerts */}
                        {triggeredAlerts.map((a) => {
                          const condLabel = a.condition === 'above' ? 'went above' : a.condition === 'below' ? 'dropped below' : a.condition === 'pct_change_up' ? 'rose by' : 'fell by';
                          const thresholdStr = a.condition.includes('pct') ? `${a.threshold}%` : `$${a.threshold.toLocaleString()}`;
                          const timeStr = a.triggeredAt ? new Date(a.triggeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' : '';
                          return (
                            <Link key={a.id} href={`/stocks/${a.symbol}`} onClick={() => setAlertsOpen(false)} className="flex items-start gap-3 px-4 py-3 hover:bg-card-hover-bg transition-colors">
                              <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Bell size={14} className="text-amber-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white">
                                  <span className="font-mono font-semibold text-sky-400">{a.symbol}</span>{' '}
                                  <span className="text-slate-300">{condLabel} {thresholdStr}</span>
                                </p>
                                {a.currentPrice != null && (
                                  <p className="text-xs text-fg-subtle mt-0.5">Current: ${a.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                )}
                                {timeStr && <p className="text-[10px] text-slate-600 mt-0.5">{timeStr}</p>}
                              </div>
                            </Link>
                          );
                        })}
                        {/* Volume-spike scanner alerts — only watchlist tickers
                            since the user's last bell-clear, severity >= elevated.
                            Tighter color than the price-alert amber so the
                            two surfaces are distinguishable at a glance:
                            scanner = red icon (intensity = volume), price
                            = amber bell (threshold crossed). */}
                        {scannerAlerts.map((a) => {
                          const sevLabel = a.severity === 'extreme' ? 'extreme spike'
                            : a.severity === 'high' ? 'high spike'
                            : 'volume spike';
                          const reasonLabel = a.reason ? a.reason.replace(/_/g, ' ') : '';
                          const timeStr = a.triggeredAt ? timeAgo(a.triggeredAt) : '';
                          const absTimeStr = a.triggeredAt ? new Date(a.triggeredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET' : '';
                          const sevColor = a.severity === 'extreme' ? 'text-red-400' : a.severity === 'high' ? 'text-orange-400' : 'text-sky-400';
                          const sevBg = a.severity === 'extreme' ? 'bg-red-500/15 border-red-500/30' : a.severity === 'high' ? 'bg-orange-500/15 border-orange-500/30' : 'bg-sky-500/15 border-sky-500/30';
                          return (
                            <Link key={`scanner-${a.ticker}-${a.triggeredAt}`} href={`/stocks/${a.ticker}`} onClick={() => setAlertsOpen(false)} className="flex items-start gap-3 px-4 py-3 hover:bg-card-hover-bg transition-colors">
                              <div className={cn('w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5', sevBg)}>
                                <Zap size={14} className={sevColor} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white">
                                  <span className="font-mono font-semibold text-sky-400">{a.ticker}</span>{' '}
                                  <span className="text-slate-300">{sevLabel}</span>
                                  {reasonLabel && <span className="text-fg-muted"> · {reasonLabel}</span>}
                                </p>
                                {(a.peakChangePct != null || a.peakRvol != null || a.currentPrice != null) && (
                                  <p className="text-xs text-slate-300 mt-0.5 font-mono">
                                    {a.peakChangePct != null && (
                                      <span className={a.peakChangePct >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                        {a.peakChangePct >= 0 ? '+' : ''}{a.peakChangePct.toFixed(1)}%
                                      </span>
                                    )}
                                    {a.peakChangePct != null && a.peakRvol != null && <span className="text-fg-subtle"> · </span>}
                                    {a.peakRvol != null && <span className="text-sky-300">{a.peakRvol.toFixed(1)}× rvol</span>}
                                    {a.currentPrice != null && <span className="text-slate-300"> · ${a.currentPrice.toFixed(2)}</span>}
                                  </p>
                                )}
                                {timeStr && <p className="text-[11px] text-fg-muted mt-0.5" title={absTimeStr}>{timeStr}</p>}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                    {/* Mute footer (issue: alerts bell mute buttons). Per-channel,
                        per-duration. Skips real-time price/volume/risk alerts; does
                        not affect digests or transactional emails. */}
                    <div className="px-3 py-2.5 border-t border-default bg-surface/40 space-y-1.5">
                      {(['email', 'push'] as const).map((channel) => {
                        const until = channel === 'email' ? muteStatus.emailMutedUntil : muteStatus.pushMutedUntil;
                        const muted = until != null;
                        const label = channel === 'email' ? 'Email' : 'Push';
                        const untilDate = until ? new Date(until) : null;
                        const untilLabel = untilDate
                          ? untilDate.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'
                          : '';
                        return (
                          <div key={channel} className="flex items-center gap-2 text-[11px]">
                            <span className="text-fg-muted w-12 flex-shrink-0">{label}</span>
                            {muted ? (
                              <>
                                <span className="text-amber-300 flex-1 truncate" title={untilLabel}>Muted until {untilLabel}</span>
                                <button
                                  onClick={() => void handleUnmute(channel)}
                                  disabled={mutePending === channel}
                                  className="px-2 py-1 rounded bg-slate-700/60 hover:bg-slate-600/60 text-slate-200 disabled:opacity-40"
                                >
                                  Unmute
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => void handleMute(channel, 'today')}
                                  disabled={mutePending === channel}
                                  className="flex-1 px-2 py-1 rounded bg-card-bg hover:bg-slate-700 border border-default text-slate-200 disabled:opacity-40"
                                >
                                  Today
                                </button>
                                <button
                                  onClick={() => void handleMute(channel, 'week')}
                                  disabled={mutePending === channel}
                                  className="flex-1 px-2 py-1 rounded bg-card-bg hover:bg-slate-700 border border-default text-slate-200 disabled:opacity-40"
                                >
                                  Week
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </>
                )}
              </div>
            )}

            {loading ? (
              <div className="w-7 h-7 rounded-full bg-card-bg animate-pulse" />
            ) : user ? (
              <div
                ref={userMenuRef}
                className="relative"
                onMouseEnter={() => {
                  // Match Stocks/Portfolio hover cadence (200ms enter).
                  if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                  hoverTimerRef.current = setTimeout(() => { setUserMenuOpen(true); setOpenNav(null); }, 200);
                }}
                onMouseLeave={() => {
                  if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                  hoverTimerRef.current = setTimeout(() => setUserMenuOpen(false), 400);
                }}
              >
                <button
                  onClick={() => { setUserMenuOpen(!userMenuOpen); setOpenNav(null); setMobileOpen(false); }}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/5 transition-colors"
                >
                  {user.avatarUrl
                    ? <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"><img src={user.avatarUrl} className="w-full h-full object-cover" alt={user.email} /></div>
                    : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{(user.username || user.email || '?')[0].toUpperCase()}</div>
                  }
                  <ChevronDown size={14} className="text-fg-muted hidden sm:block" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-card-bg border border-default rounded-xl shadow-2xl py-1.5 z-50">
                    <div className="px-4 py-2.5 border-b border-default relative">
                      {/* Plan: Admin Panel shortcut in the corner of the user
                          menu — icon-only, red-tinted, admin-only. Sits in
                          the header corner so it's glanceable but doesn't
                          compete with the main nav links below. */}
                      {isAdmin && (
                        <Link
                          href="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          aria-label="Admin Panel"
                          title="Admin Panel"
                          className="absolute top-2 right-2 p-1.5 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30 hover:border-red-500/50 transition-colors"
                        >
                          <Shield size={14} />
                        </Link>
                      )}
                      <div className="text-sm font-medium text-white truncate pr-8">@{user.username}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {/* Role takes precedence over subscription tier for
                         *  admin / staff — their DB tier may be anything
                         *  ('free' for internal accounts, for instance),
                         *  but what matters operationally is their role.
                         *  For regular users, show the subscription tier as
                         *  before. Colors distinguish the three kinds of
                         *  badge (role = red/violet, tier = gold/fuchsia). */}
                        {user.role === 'admin' ? (
                          <span className="text-xs px-2 py-0.5 rounded font-medium bg-rose-500/20 text-rose-300 border border-rose-400/30">
                            Admin
                          </span>
                        ) : user.role === 'staff' ? (
                          <span className="text-xs px-2 py-0.5 rounded font-medium bg-violet-500/20 text-violet-300 border border-violet-400/30">
                            Staff
                          </span>
                        ) : (
                          <span className={cn(
                            'text-xs px-2 py-0.5 rounded font-medium',
                            user.subscriptionTier === 'vip'
                              ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30'
                              : user.subscriptionTier === 'full'
                              ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30'
                              : user.subscriptionTier === 'pro'
                              ? 'bg-gold/20 text-gold'
                              : 'bg-slate-700 text-fg-muted'
                          )}>
                            {user.subscriptionTier === 'vip' ? 'VIP' : user.subscriptionTier === 'full' ? 'Full' : user.subscriptionTier === 'pro' ? 'Pro' : 'Free'}
                          </span>
                        )}
                      </div>
                    </div>
                    <Link
                      href="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-card-hover-bg transition-colors"
                    >
                      <Settings size={15} /> Account Settings
                    </Link>
                    <Link
                      href="/account/trade-plans"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-card-hover-bg transition-colors"
                    >
                      <Target size={15} /> My Trade Plans
                    </Link>
                    <Link
                      href="/account/configurations"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-card-hover-bg transition-colors"
                    >
                      <SlidersHorizontal size={15} /> Configurations
                    </Link>
                    {(isAdmin || user.subscriptionTier === 'pro' || user.subscriptionTier === 'full') && (
                      <Link
                        href="/developer"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-card-hover-bg transition-colors"
                      >
                        <Code2 size={15} /> Developer API
                      </Link>
                    )}
                    {/* Admin Panel was here — moved to a dedicated red-tinted
                        Shield icon in the top-right toolbar (visible only for
                        admins). Keeps the user menu focused on account-level
                        actions; admin entry is one click from anywhere. */}
                    {/* Theme picker */}
                    <div className="px-4 py-2.5 border-t border-default">
                      <div className="flex items-center gap-2 mb-2">
                        <Palette size={13} className="text-fg-subtle" />
                        <span className="text-[11px] text-fg-subtle uppercase tracking-wide">Theme</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {([
                          { key: 'dark' as const, color: '#0f172a', border: '#334155', label: 'Dark' },
                          { key: 'contrast' as const, color: '#1a1a2e', border: '#3d3d5c', label: 'Contrast' },
                          { key: 'light' as const, color: '#f8fafc', border: '#e2e8f0', label: 'Light' },
                          { key: 'gray' as const, color: '#27272a', border: '#52525b', label: 'Gray' },
                          { key: 'blue' as const, color: '#dbeafe', border: '#93c5fd', label: 'Blue' },
                        ] as const).map(({ key, color, border, label }) => (
                          <button
                            key={key}
                            onClick={() => setTheme(key)}
                            title={label}
                            className={cn(
                              'w-7 h-7 rounded-lg border-2 transition-all flex-shrink-0',
                              theme === key ? 'ring-2 ring-sky-400 scale-110' : 'hover:scale-105',
                            )}
                            style={{ backgroundColor: color, borderColor: border }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Large text toggle */}
                    <div className="px-4 py-2 border-t border-default">
                      <button
                        onClick={() => setLargeText(!largeText)}
                        className="flex items-center gap-3 w-full text-sm text-slate-300 hover:text-white transition-colors"
                      >
                        <Type size={15} />
                        <span className="flex-1 text-left">Larger Text</span>
                        <div className={cn(
                          'w-8 h-[18px] rounded-full transition-colors relative',
                          largeText ? 'bg-sky-500' : 'bg-border-default',
                        )}>
                          <div className={cn(
                            'absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform',
                            largeText ? 'translate-x-[14px]' : 'translate-x-0.5',
                          )} />
                        </div>
                      </button>
                    </div>

                    {/* Square tiles toggle — flips dashboard tile chrome
                        from rounded (bevel) to square corners. Affects
                        every tile that uses `rounded-xl` inside the
                        gridstack via a globals.css override gated on
                        html.square-tiles. */}
                    <div className="px-4 py-2 border-t border-default">
                      <button
                        onClick={() => setSquareTiles(!squareTiles)}
                        className="flex items-center gap-3 w-full text-sm text-slate-300 hover:text-white transition-colors"
                      >
                        <Square size={15} />
                        <span className="flex-1 text-left">Square Tiles</span>
                        <div className={cn(
                          'w-8 h-[18px] rounded-full transition-colors relative',
                          squareTiles ? 'bg-sky-500' : 'bg-border-default',
                        )}>
                          <div className={cn(
                            'absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform',
                            squareTiles ? 'translate-x-[14px]' : 'translate-x-0.5',
                          )} />
                        </div>
                      </button>
                    </div>

                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-card-hover-bg transition-colors border-t border-default"
                    >
                      <LogOut size={15} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  href="/login"
                  className="px-3 py-1.5 text-xs font-semibold tracking-wider uppercase text-slate-300 hover:text-white transition-colors"
                >
                  LOGIN
                </Link>
                <Link
                  href="/create-account"
                  className="px-3 py-1.5 text-xs font-semibold tracking-wider uppercase bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors"
                >
                  REGISTER
                </Link>
              </div>
            )}

            {/* Mobile login icon (shown when not logged in) */}
            {!loading && !user && (
              <Link
                href="/login"
                className="lg:hidden sm:hidden p-2 text-fg-muted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                aria-label="Login"
              >
                <User size={18} />
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => { setMobileOpen(!mobileOpen); setUserMenuOpen(false); setOpenNav(null); }}
              className="lg:hidden p-2 text-fg-muted hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div ref={mobileMenuRef} className="lg:hidden border-t border-white/5 backdrop-blur-xl" style={{ backgroundColor: 'color-mix(in srgb, var(--background) 95%, transparent)' }}>
          <div className="px-4 py-3 space-y-1 overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100vh - 64px)' }}>
            {/* Login/Register at top of mobile menu */}
            {!loading && !user && (
              <div className="flex gap-2 pb-3 mb-2 border-b border-default">
                <Link
                  href="/login"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center px-3 py-2 text-xs font-semibold tracking-wider uppercase bg-card-bg text-white rounded-lg"
                >
                  LOGIN
                </Link>
                <Link
                  href="/create-account"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center px-3 py-2 text-xs font-semibold tracking-wider uppercase bg-sky-500 text-white rounded-lg"
                >
                  REGISTER
                </Link>
              </div>
            )}
            {filteredNavItems.map((item) => (
              <div key={item.label}>
                {item.href && !item.dropdown ? (
                  <Link
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-semibold tracking-wider uppercase rounded-lg',
                      item.gold ? 'text-gold' : 'text-slate-300 hover:text-white hover:bg-white/5'
                    )}
                  >
                    {item.label}
                    {item.href && renderAccessBadge(item.href)}
                  </Link>
                ) : (
                  <MobileAccordion label={item.label}>
                    {item.dropdown?.map((sub) => {
                      const SubIcon = sub.icon;
                      return (
                      <Link
                        key={sub.href}
                        href={sub.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2.5 px-6 py-2 text-sm text-fg-muted hover:text-white hover:bg-white/5 rounded-lg"
                      >
                        {SubIcon && <SubIcon size={14} className="text-slate-600 flex-shrink-0" />}
                        {sub.label}
                        {renderAccessBadge(sub.href)}
                      </Link>
                      );
                    })}
                  </MobileAccordion>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
